/**
 * GlobalSearchModal — V107.52.34
 *
 * The world-search overlay. Rendered as a sibling of the layout content
 * by GlobalSearchProvider — only mounted while open (so the search index
 * isn't built for users who never invoke it).
 *
 * Mobile: full-screen sheet. Desktop: centered card with backdrop blur.
 *
 * Keyboard:
 *   ↑ / ↓     navigate selection
 *   Enter     open selected result
 *   Esc       close (handled by useGlobalSearchKeyboard)
 *   Tab       advance focus (default browser behaviour, not stolen)
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  X,
  Loader2,
  Zap,
  Sparkles as SparklesIcon,
  CalendarDays,
  AlertCircle,
  User,
  Contact as ContactIcon,
  Briefcase,
  MessageSquare,
  Wifi,
  Tag,
  ArrowRight,
  ChevronUp,
  ChevronDown,
  CornerDownLeft,
  History,
} from 'lucide-react';
import { useGlobalSearchContext } from './GlobalSearchProvider';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';
import type { SearchEntityType, SearchResultItem } from '@/hooks/useGlobalSearch';
import { SearchFilterChips } from './SearchFilterChips';
import { SearchResultRow } from './SearchResultRow';

const SECTION_LABEL: Record<SearchEntityType, string> = {
  customer: 'Customers',
  contact: 'Contacts',
  deal: 'Deals',
  fault: 'Faults',
  job: 'Jobs',
  service_request: 'Service Requests',
  customer_service: 'Active Services',
  serial: 'Serial Numbers',
};

const SECTION_ORDER: SearchEntityType[] = [
  'customer',
  'contact',
  'deal',
  'fault',
  'job',
  'service_request',
  'customer_service',
  'serial',
];

// "Quick access" tiles shown in the empty state (no query).
const QUICK_ACCESS = [
  { label: "Today's jobs", icon: CalendarDays, path: '/home/schedule', color: 'text-purple-300' },
  { label: 'Open faults', icon: AlertCircle, path: '/home/faults', color: 'text-rose-300' },
  { label: 'Active deals', icon: SparklesIcon, path: '/home/deals', color: 'text-amber-300' },
  { label: 'All customers', icon: User, path: '/home/customers', color: 'text-sky-300' },
];

export function GlobalSearchModal() {
  const navigate = useNavigate();
  const {
    close,
    query,
    setQuery,
    activeFilter,
    setActiveFilter,
    recentSearches,
    addRecentSearch,
    clearRecentSearches,
  } = useGlobalSearchContext();

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const results = useGlobalSearch(query, activeFilter);

  // Flatten results into a single ordered list for keyboard navigation.
  // Order: Active Now → each section in SECTION_ORDER.
  const flatResults: SearchResultItem[] = useMemo(() => {
    const out: SearchResultItem[] = [...results.active];
    for (const type of SECTION_ORDER) {
      out.push(...results.groups[type]);
    }
    return out;
  }, [results]);

  // Reset selection when results shape changes.
  useEffect(() => {
    setSelectedIndex(0);
  }, [query, activeFilter]);

  // Focus input on mount.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Scroll selected row into view as user arrows through results.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLElement>(`[data-result-index="${selectedIndex}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  function handleSelect(item: SearchResultItem, openInNewTab = false) {
    addRecentSearch(query);
    if (openInNewTab) {
      window.open(item.navigate, '_blank', 'noopener');
    } else {
      navigate(item.navigate);
      close();
    }
  }

  function handleQuickAccess(path: string) {
    navigate(path);
    close();
  }

  function handleRecentSearch(term: string) {
    setQuery(term);
    inputRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, Math.max(0, flatResults.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && flatResults.length > 0) {
      e.preventDefault();
      const item = flatResults[selectedIndex];
      if (item) handleSelect(item, e.metaKey || e.ctrlKey);
    }
  }

  const hasQuery = query.trim().length > 0;
  const hasResults = flatResults.length > 0;

  return (
    <AnimatePresence>
      <motion.div
        key="search-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-start justify-center safe-top"
        onMouseDown={(e) => {
          // Click-outside-to-close (only when clicking the backdrop, not the modal).
          if (e.target === e.currentTarget) close();
        }}
      >
        <motion.div
          key="search-card"
          initial={{ opacity: 0, y: -8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.98 }}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          className="w-full sm:max-w-2xl sm:mt-16 sm:mb-8 sm:rounded-2xl bg-surface-card border-theme border-x sm:border shadow-2xl flex flex-col h-full sm:h-auto sm:max-h-[80vh] overflow-hidden"
        >
          {/* Header: input + close */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-theme-soft bg-surface-card">
            <Search className="w-4 h-4 text-theme-muted shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search anything — name, contact, phone, serial, AVC…"
              className="flex-1 bg-transparent text-base text-theme-primary placeholder-theme-muted focus:outline-none"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {results.serialsLoading && (
              <Loader2 className="w-4 h-4 text-theme-muted animate-spin shrink-0" />
            )}
            <button
              type="button"
              onClick={close}
              aria-label="Close search"
              className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-theme-tertiary hover:text-theme-primary hover:bg-surface-hover transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Filter chips — only when there's a query (clean empty state) */}
          {hasQuery && (
            <div className="px-4 pt-3 border-b border-theme-soft">
              <SearchFilterChips
                active={activeFilter}
                onSelect={setActiveFilter}
                counts={results.counts}
                totalCount={results.totalCount}
              />
            </div>
          )}

          {/* Body */}
          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
            {/* No query → recent searches + quick access */}
            {!hasQuery && (
              <EmptyState
                recentSearches={recentSearches}
                onRecentClick={handleRecentSearch}
                onClearRecent={clearRecentSearches}
                onQuickAccess={handleQuickAccess}
              />
            )}

            {/* Query but no results */}
            {hasQuery && !hasResults && !results.loading && (
              <NoResults query={query} serialsLoading={results.serialsLoading} />
            )}

            {/* Active Now section */}
            {hasQuery && results.active.length > 0 && (
              <Section
                label="Active Now"
                icon={Zap}
                iconColor="text-emerald-300"
                items={results.active}
                offset={0}
                selectedIndex={selectedIndex}
                onSelect={handleSelect}
                onHover={setSelectedIndex}
              />
            )}

            {/* Per-type sections */}
            {hasQuery &&
              SECTION_ORDER.map((type) => {
                const items = results.groups[type];
                if (items.length === 0) return null;
                // Compute global flat-list offset for this section (so keyboard
                // selectedIndex maps correctly across sections).
                const offset = computeOffset(results, type);
                // "More available — filter to see" hint. Only meaningful in the
                // combined ("all") view, where selecting the filter genuinely
                // reveals more (the filtered view uses a much larger cap and the
                // footer's "Showing top N of M" already covers any overflow).
                const activeOfType = results.active.filter((i) => i.type === type).length;
                const hasMore =
                  activeFilter === 'all' &&
                  results.counts[type] > items.length + activeOfType;
                return (
                  <Section
                    key={type}
                    label={SECTION_LABEL[type]}
                    icon={SECTION_ICON[type]}
                    iconColor={SECTION_ICON_COLOR[type]}
                    items={items}
                    offset={offset}
                    selectedIndex={selectedIndex}
                    onSelect={handleSelect}
                    onHover={setSelectedIndex}
                    hasMore={hasMore}
                  />
                );
              })}

            {hasQuery && results.totalCount > 0 && (
              <p className="text-center text-[11px] text-theme-muted pt-2">
                {results.totalCount > flatResults.length
                  ? `Showing top ${flatResults.length} of ${results.totalCount} · refine query for more`
                  : `${results.totalCount} ${results.totalCount === 1 ? 'result' : 'results'}`}
              </p>
            )}
          </div>

          {/* Footer with keyboard hints (desktop only) */}
          <div className="hidden sm:flex items-center justify-between gap-3 px-4 py-2 border-t border-theme-soft bg-surface-card text-[10px] text-theme-muted">
            <div className="flex items-center gap-3">
              <KeyHint icon={ChevronUp}>navigate</KeyHint>
              <KeyHint icon={ChevronDown}>navigate</KeyHint>
              <KeyHint icon={CornerDownLeft}>open</KeyHint>
              <span>esc to close</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span>Press</span>
              <kbd className="px-1.5 py-0.5 rounded bg-surface-hover border border-theme-soft font-mono text-[9px] text-theme-tertiary">
                /
              </kbd>
              <span>or</span>
              <kbd className="px-1.5 py-0.5 rounded bg-surface-hover border border-theme-soft font-mono text-[9px] text-theme-tertiary">
                ⌘K
              </kbd>
              <span>anywhere to search</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// Sections + helpers
// ---------------------------------------------------------------------------

const SECTION_ICON: Record<SearchEntityType, typeof User> = {
  customer: User,
  contact: ContactIcon,
  deal: SparklesIcon,
  fault: AlertCircle,
  job: Briefcase,
  service_request: MessageSquare,
  customer_service: Wifi,
  serial: Tag,
};

const SECTION_ICON_COLOR: Record<SearchEntityType, string> = {
  customer: 'text-sky-300',
  contact: 'text-teal-300',
  deal: 'text-amber-300',
  fault: 'text-rose-300',
  job: 'text-purple-300',
  service_request: 'text-violet-300',
  customer_service: 'text-emerald-300',
  serial: 'text-cyan-300',
};

function computeOffset(
  results: ReturnType<typeof useGlobalSearch>,
  type: SearchEntityType,
): number {
  let offset = results.active.length;
  for (const t of SECTION_ORDER) {
    if (t === type) break;
    offset += results.groups[t].length;
  }
  return offset;
}

interface SectionProps {
  label: string;
  icon: typeof User;
  iconColor: string;
  items: SearchResultItem[];
  offset: number;
  selectedIndex: number;
  /** True when this group hit the per-group cap — shows a "more" hint. */
  hasMore?: boolean;
  onSelect: (item: SearchResultItem, openInNewTab?: boolean) => void;
  onHover: (index: number) => void;
}

function Section({
  label,
  icon: Icon,
  iconColor,
  items,
  offset,
  selectedIndex,
  hasMore,
  onSelect,
  onHover,
}: SectionProps) {
  return (
    <section>
      <div className="flex items-center gap-1.5 mb-2 px-1">
        <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-theme-muted">
          {label}
        </h3>
        {hasMore && (
          <span className="text-[10px] text-theme-muted">· more available — filter to see</span>
        )}
      </div>
      <div className="space-y-1.5">
        {items.map((item, i) => {
          const globalIndex = offset + i;
          return (
            <div key={`${item.type}:${item.id}`} data-result-index={globalIndex}>
              <SearchResultRow
                item={item}
                isSelected={globalIndex === selectedIndex}
                onSelect={() => onSelect(item)}
                onHover={() => onHover(globalIndex)}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Empty state + no results
// ---------------------------------------------------------------------------

interface EmptyStateProps {
  recentSearches: string[];
  onRecentClick: (term: string) => void;
  onClearRecent: () => void;
  onQuickAccess: (path: string) => void;
}

function EmptyState({ recentSearches, onRecentClick, onClearRecent, onQuickAccess }: EmptyStateProps) {
  return (
    <div className="space-y-5">
      {recentSearches.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="flex items-center gap-1.5">
              <History className="w-3.5 h-3.5 text-theme-muted" />
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-theme-muted">
                Recent
              </h3>
            </div>
            <button
              type="button"
              onClick={onClearRecent}
              className="text-[10px] text-theme-muted hover:text-theme-secondary transition-colors"
            >
              Clear
            </button>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {recentSearches.map((term) => (
              <button
                key={term}
                type="button"
                onClick={() => onRecentClick(term)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-hover/60 hover:bg-surface-hover border border-theme-soft hover:border-theme text-xs text-theme-secondary hover:text-theme-primary transition-all"
              >
                <Search className="w-3 h-3 text-theme-muted" />
                {term}
              </button>
            ))}
          </div>
        </section>
      )}

      <section>
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-theme-muted mb-2 px-1">
          Quick Access
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {QUICK_ACCESS.map((q) => {
            const QIcon = q.icon;
            return (
              <button
                key={q.path}
                type="button"
                onClick={() => onQuickAccess(q.path)}
                className="group flex items-center gap-2 px-3 py-2.5 rounded-lg bg-surface-card/50 hover:bg-surface-hover border border-theme-soft hover:border-theme transition-all text-left"
              >
                <QIcon className={`w-4 h-4 ${q.color}`} />
                <span className="text-sm text-theme-secondary group-hover:text-theme-primary flex-1 truncate">
                  {q.label}
                </span>
                <ArrowRight className="w-3.5 h-3.5 text-theme-muted opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            );
          })}
        </div>
      </section>

      <p className="text-[11px] text-theme-muted text-center pt-2">
        Try a customer or contact name, phone, address, account number, serial, or AVC.
      </p>
    </div>
  );
}

function NoResults({ query, serialsLoading }: { query: string; serialsLoading: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-12 h-12 rounded-full bg-surface-hover flex items-center justify-center mb-3">
        <Search className="w-5 h-5 text-theme-muted" />
      </div>
      <p className="text-sm font-medium text-theme-secondary">
        {serialsLoading ? 'Searching…' : `No results for "${query}"`}
      </p>
      {!serialsLoading && (
        <p className="text-xs text-theme-muted mt-1 max-w-xs">
          Try a different spelling, or search by phone number, address, or account number.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Footer key hint
// ---------------------------------------------------------------------------

function KeyHint({ icon: Icon, children }: { icon: typeof User; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1">
      <kbd className="inline-flex items-center justify-center w-4 h-4 rounded bg-surface-hover border border-theme-soft text-theme-tertiary">
        <Icon className="w-2.5 h-2.5" />
      </kbd>
      <span className="text-theme-muted">{children}</span>
    </span>
  );
}

